import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  Box,
} from '@mui/material';
import { Requisite, RequisiteFormData } from '../../../types/requisite';
import { Trader } from '../../../types/trader';
import { PaymentSystem } from '../../../types/common/payment';
import { Bank } from '../../../types/common/payment';

interface RequisiteFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  editingRequisite: Requisite | null;
  formData: RequisiteFormData;
  setFormData: (data: RequisiteFormData) => void;
  traders: Trader[];
  paymentSystems: PaymentSystem[];
  banks: Bank[];
}

export default function RequisiteForm({
  open,
  onClose,
  onSubmit,
  editingRequisite,
  formData,
  setFormData,
  traders,
  paymentSystems,
  banks,
}: RequisiteFormProps) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEnforceFocus
      disableAutoFocus
    >
      <DialogTitle sx={{ 
        backgroundColor: '#1a1a1a',
        color: 'white',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        {editingRequisite ? 'Редактирование реквизитов' : 'Добавление реквизитов'}
      </DialogTitle>
      <DialogContent sx={{ 
        backgroundColor: '#1a1a1a',
        color: 'white',
        padding: '16px 24px',
        '&:first-of-type': {
          paddingTop: '16px'
        }
      }}>
        <Stack spacing={2}>
          <TextField
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                backgroundColor: '#1a1a1a',
                padding: '0 4px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Трейдер</InputLabel>
            <Select
              value={formData.trader_id}
              label="Трейдер"
              onChange={(e) => setFormData({ ...formData, trader_id: e.target.value })}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {traders.map((trader) => (
                <MenuItem key={trader.id} value={trader.id}>
                  {trader.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Платежная система</InputLabel>
            <Select
              value={formData.payment_system_id}
              label="Платежная система"
              onChange={(e) => setFormData({ ...formData, payment_system_id: e.target.value })}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {paymentSystems.map((ps) => (
                <MenuItem key={ps.id} value={ps.id}>
                  {ps.token}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Номер"
            value={formData.number}
            onChange={(e) => setFormData({ ...formData, number: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Дополнительный номер"
            value={formData.second_number || ''}
            onChange={(e) => setFormData({ ...formData, second_number: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Владелец"
            value={formData.holder_name}
            onChange={(e) => setFormData({ ...formData, holder_name: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Банк</InputLabel>
            <Select
              value={formData.bank_id}
              label="Банк"
              onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Минимальный лимит транзакции"
            type="number"
            value={formData.min_transaction_limit}
            onChange={(e) => setFormData({ ...formData, min_transaction_limit: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Максимальный лимит транзакции"
            type="number"
            value={formData.max_transaction_limit}
            onChange={(e) => setFormData({ ...formData, max_transaction_limit: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Лимит по количеству"
            type="number"
            value={formData.counter_limit}
            onChange={(e) => setFormData({ ...formData, counter_limit: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Лимит по сумме"
            type="number"
            value={formData.amount_limit}
            onChange={(e) => setFormData({ ...formData, amount_limit: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Текущий счетчик"
            type="number"
            value={formData.current_counter}
            onChange={(e) => setFormData({ ...formData, current_counter: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Текущая сумма"
            type="number"
            value={formData.current_amount}
            onChange={(e) => setFormData({ ...formData, current_amount: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Ожидаемая сумма"
            type="number"
            value={formData.pending_amount}
            onChange={(e) => setFormData({ ...formData, pending_amount: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <TextField
            label="Ожидаемый счетчик"
            type="number"
            value={formData.pending_counter}
            onChange={(e) => setFormData({ ...formData, pending_counter: Number(e.target.value) })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <FormControl>
            <Typography component="label" display="flex" alignItems="center" gap={1} sx={{ color: 'white' }}>
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                disabled={Boolean(editingRequisite?.is_blocked_by_trader)}
              />
              Активен
            </Typography>
            {editingRequisite?.is_blocked_by_trader && (
              <Typography variant="caption" color="error.main">
                Сначала снимите UPI-блокировку в таблице реквизитов
              </Typography>
            )}
          </FormControl>
          <FormControl>
            <Typography component="label" display="flex" alignItems="center" gap={1} sx={{ color: 'white' }}>
              <Switch
                checked={formData.pre_deled}
                onChange={(e) => setFormData({ ...formData, pre_deled: e.target.checked })}
              />
              Предварительно удален
            </Typography>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        backgroundColor: '#1a1a1a',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        padding: 2
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Отмена
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
            },
          }}
        >
          {editingRequisite ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
